import { Injectable } from '@angular/core';
import {
  IAnalyticsOptions,
  ITrackingAction,
  ITrackingObject,
  ITrackingProperties,
} from './models';
import { I2gNgTrackingBuilder } from './i2g-ng-tracking.builder';
import { WindowRefService } from 'i2g-ng-utils';

const md5 = require('md5');

@Injectable({
  providedIn: 'root',
})
export class I2gNgTrackingService {
  private static _loadedAnalytics: boolean;
  private static _options: IAnalyticsOptions;
  private static _traits: ITrackingProperties;
  private static _window: any;

  public trackingObject: any;

  constructor(
    private _trackingBuilder: I2gNgTrackingBuilder,
    private _windowRefService: WindowRefService
  ) {
    I2gNgTrackingService._window = this._windowRefService.nativeWindow;
  }

  static withObject = (trackingObject) => ({
    provide: I2gNgTrackingService,
    deps: [I2gNgTrackingBuilder, WindowRefService],
    useFactory: (
      trackingBuilder: I2gNgTrackingBuilder,
      windowRefService: WindowRefService
    ) => {
      const service = new I2gNgTrackingService(
        trackingBuilder,
        windowRefService
      );
      service.trackingObject = trackingObject;
      return service;
    },
  });

  /**
   * Initialize the Client
   *
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#load-options
   * @param segmentWriteKey Segment write key
   * @param options Analytics.js options
   */
  load(segmentWriteKey: string, options: IAnalyticsOptions) {
    if (
      !Object.prototype.hasOwnProperty.call(
        I2gNgTrackingService._window,
        'analytics'
      )
    ) {
      throw new Error('Analytics JS is missing');
    }

    if (!I2gNgTrackingService._loadedAnalytics) {
      I2gNgTrackingService._window.analytics.load(segmentWriteKey);

      I2gNgTrackingService._loadedAnalytics = true;
      I2gNgTrackingService._options = options;
    }
  }

  /**
   * Generate unique id for identify calls.
   *
   * Currently, the Segment userId field, which is supposed to be a unique identifier,
   * contains the user’s email address. We want to stop using the user’s email address in this field, and instead
   * populate the field with the result of an MD5 hash of the following two i2g fields: accountuser.userid and
   * accountuser.accountid. The hash should have a dash delimiter to avoid collisions: [hash of userid]-[hash of
   * accountid]
   *
   * @param userId User ID
   * @param accountId Account ID
   */
  generateUniqueId(userId: string, accountId: string): string {
    if (!userId || !accountId) {
      throw new Error('User ID and Account ID are required');
    }

    return `${md5(userId)}-${md5(accountId)}`;
  }

  /**
   * The identify method is how you associate your users and their actions to a recognizable userId and traits.
   *
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#identify
   * @param userId The database ID for the user. If you don’t know who the user is yet, you can omit the userId and
   *   just record traits.
   * @param traits A dictionary of traits you know about the user, like their email or name.
   */
  identify(userId: string, traits?: ITrackingProperties): void {
    if (traits) {
      I2gNgTrackingService._traits = traits;
    }
    I2gNgTrackingService._window.analytics.identify(
      userId,
      traits,
      this._trackingBuilder.buildContext(I2gNgTrackingService._options, traits)
    );
  }

  /**
   * The page method lets you record page views on your website, along with optional extra information about the page
   * being viewed. Because some destinations require a page call to instantiate their libraries, you must call page at
   * least once per page load! You may call it more than once if needed, (eg, on virtual page changes in a single page
   * app).
   *
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#page
   * @param pageName The name of the page.
   * @param properties A dictionary of properties of the page. Note: url, title, referrer and path are collected
   *   automatically!
   */
  trackPage(pageName: string, properties: ITrackingProperties = null): void {
    const category = null;

    I2gNgTrackingService._window.analytics.page(
      category,
      pageName,
      properties,
      this._trackingBuilder.buildContext(
        I2gNgTrackingService._options,
        I2gNgTrackingService._traits
      )
    );
  }

  /**
   * The track method lets you record any actions your users perform.
   *
   * We use a combination of tracking object and action to construct the final tracking event.
   * For example if we want to track a button click inside a document, the tracking object would be
   * the document and the tracking action would be button tapped. The final event name will be a
   * combination of object and action name, the event properties would be combination of properties from
   * both tracking object and tracking action.
   *
   * @see https://segment.com/docs/connections/sources/catalog/libraries/website/javascript/#track
   * @param trackingObjects A dictionary of properties for identifying an entity associated with the event.
   * @param trackingAction A dictionary of properties for identifying an action associated with the event.
   * @param additionalProperties A dictionary of additional properties for an event.
   */
  trackEvent(
    _trackingObjects: ITrackingObject | ITrackingObject[],
    trackingAction: ITrackingAction | any,
    additionalProperties?: ITrackingProperties
  ): void {
    const trackingObjects = _trackingObjects || this.trackingObject;

    if (trackingObjects && trackingAction) {
      const trackingEvent = this._trackingBuilder.buildTrackingEvent(
        trackingObjects,
        trackingAction,
        additionalProperties
      );

      I2gNgTrackingService._window.analytics.track(
        trackingEvent.name,
        trackingEvent.properties,
        this._trackingBuilder.buildContext(
          I2gNgTrackingService._options,
          I2gNgTrackingService._traits
        )
      );
    }
  }
}
